import type { PublicEventDetail } from "@homesite/api-contract";
import { queryKeys } from "@homesite/queries";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/ApiClientContext";

export interface EventByHashData {
	event: PublicEventDetail | null;
	loading: boolean;
	error: Error | null;
}

/**
 * Fetches anonymized event metadata and embedded proof for a given event hash.
 *
 * Returns only data fields — callbacks (onBack, onCopyHash, onGenerateReport)
 * are owned by the route so they can wire navigation and side effects.
 */
export function useEventByHash(hash: string): EventByHashData {
	const client = useApiClient();

	const query = useQuery({
		queryKey: queryKeys.publicVerification.events.byHash(hash),
		queryFn: async () => {
			const result = await client.publicVerification.getEventByHash({
				params: { hash },
			});
			if (result.status === 200) {
				return result.body;
			}
			throw new Error(
				result.status === 404 ? "Event not found" : "Failed to fetch event",
			);
		},
		enabled: hash.length > 0,
	});

	return {
		event: query.data ?? null,
		loading: query.isLoading,
		error: query.error,
	};
}
